import GeoJSON from 'ol/format/GeoJSON';

import type { StacBbox } from '../canvas-map-ol.utils';
import type { CanvasStacItem } from './canvas-stac-api.service';

function isValidBbox(bbox: StacBbox): boolean {
  const [minX, minY, maxX, maxY] = bbox;
  return minX < maxX && minY < maxY;
}

/** Normalize flat STAC bbox (2D or 3D) to axis-aligned CRS84 extent. */
export function normalizeFlatStacBbox(raw: number[]): StacBbox | null {
  if (!raw?.length || raw.length < 4) return null;

  const nums = raw.map(Number);
  if (nums.some((n) => !Number.isFinite(n))) return null;

  let west: number;
  let south: number;
  let east: number;
  let north: number;

  if (nums.length >= 6) {
    west = nums[0];
    south = nums[1];
    east = nums[3];
    north = nums[4];
  } else {
    [west, south, east, north] = nums;
  }

  const bbox: StacBbox = [
    Math.min(west, east),
    Math.min(south, north),
    Math.max(west, east),
    Math.max(south, north),
  ];

  return isValidBbox(bbox) ? bbox : null;
}

function bboxFromNestedField(bbox: unknown): StacBbox | null {
  if (!Array.isArray(bbox) || !bbox.length) return null;

  if (typeof bbox[0] === 'number') {
    return normalizeFlatStacBbox(bbox as number[]);
  }

  if (Array.isArray(bbox[0])) {
    for (const entry of bbox as number[][]) {
      const normalized = normalizeFlatStacBbox(entry);
      if (normalized) return normalized;
    }
  }

  return null;
}

function bboxFromGeometry(geometry: unknown): StacBbox | null {
  if (!geometry || typeof geometry !== 'object') return null;

  try {
    const format = new GeoJSON();
    const olGeom = format.readGeometry(geometry as object, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:4326',
    });
    const extent = olGeom.getExtent();
    if (extent.length < 4 || !extent.every((n) => Number.isFinite(n))) {
      return null;
    }
    return normalizeFlatStacBbox(extent);
  } catch {
    return null;
  }
}

function bboxFromProperties(
  properties: Record<string, unknown> | undefined,
): StacBbox | null {
  if (!properties) return null;

  const minX = Number(properties['bounds_min_x']);
  const minY = Number(properties['bounds_min_y']);
  const maxX = Number(properties['bounds_max_x']);
  const maxY = Number(properties['bounds_max_y']);
  if (![minX, minY, maxX, maxY].every((n) => Number.isFinite(n))) {
    return null;
  }

  return normalizeFlatStacBbox([minX, minY, maxX, maxY]);
}

/**
 * Resolve a plot extent for a STAC item (ui-canvas uses `item.bbox` directly;
 * many catalogues ship placeholder bboxes and valid geometry instead).
 */
export function resolveStacItemBbox(item: CanvasStacItem): StacBbox | null {
  const fromBboxField = bboxFromNestedField(item.bbox);
  if (fromBboxField) return fromBboxField;

  const fromGeometry = bboxFromGeometry(item.geometry);
  if (fromGeometry) return fromGeometry;

  const fromProperties = bboxFromProperties(item.properties);
  if (fromProperties) return fromProperties;

  for (const asset of Object.values(item.assets ?? {})) {
    const assetBbox = bboxFromNestedField(
      (asset as { bbox?: unknown }).bbox,
    );
    if (assetBbox) return assetBbox;
  }

  return null;
}
